import { getDateLabel, getSmartDateLabel } from '@griply/utils';
import { isBefore, isSameDay } from 'date-fns';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'shared/assets/icons';
import {
  DatepickerWithOptions,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  TaskMetaInteraction,
} from 'shared/components/ui/task-meta-interaction';
import { useToday } from 'shared/contexts/today';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { InteractionType } from 'shared/types/data-type';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';

import * as Styled from './task-meta-interaction-due-date.style';

export type TaskMetaInteractionDueDateProps = Omit<
  React.ComponentProps<typeof TaskMetaInteraction>,
  'start' | 'onClick' | 'label' | 'selected' | 'tooltipLabel'
> & {
  value?: Timestamp;
  initialDeadline?: Timestamp;
  hasTimeslot?: boolean;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  onChange: (date?: Timestamp) => void;
  isDeadline?: boolean;
  canRemove?: boolean;
  hasIcon?: boolean;
  highlight?: boolean;
  isCompleted?: boolean;
  tomorrowLabelAllowed?: boolean;
};

export const TaskMetaInteractionDueDate: React.FC<
  TaskMetaInteractionDueDateProps
> = ({
  value,
  initialDeadline,
  hasTimeslot,
  dateFormat,
  weekStartsOn,
  onChange,
  isDeadline,
  canRemove,
  hasIcon,
  highlight,
  tomorrowLabelAllowed,
  isCompleted,

  ...rest
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();
  const today = useToday();

  const { due, overdue } = useMemo(() => {
    if (!value) {
      return { due: undefined, overdue: false };
    }

    if (isCompleted) {
      return { due: getDateLabel(value, today), overdue: false };
    }

    const label = getSmartDateLabel(value, today, t, {
      enableWeekdays: tomorrowLabelAllowed,
    });
    const overdue = isBefore(value, today) && !isSameDay(value, today);

    return { due: label, overdue };
  }, [value, today, tomorrowLabelAllowed, t, isCompleted]);

  const dateChanged = useMemo(
    () => initialDeadline && value && !isSameDay(initialDeadline, value),
    [initialDeadline, value],
  );

  const _onChange = (date?: Timestamp) => {
    onChange(date);
    closeMenu();
  };

  return (
    <Styled.Container ref={containerRef}>
      <TaskMetaInteraction
        color={
          isCompleted
            ? Colors.Inherit
            : overdue
              ? Colors.Error
              : dateChanged
                ? Colors.Warning
                : due && highlight
                  ? Colors.TextPrimary
                  : Colors.Inherit
        }
        start={hasIcon ? <Icon icon={Calendar} /> : undefined}
        onClick={openMenu}
        label={hasTimeslot && due ? `${due},` : due}
        selected={menuOpen}
        minimalEndSpacing={hasTimeslot}
        tooltipLabel={t(
          isDeadline
            ? due
              ? 'task.deadline.tooltip.edit.label'
              : 'task.deadline.tooltip.add.label'
            : 'task.schedule.skip.tooltip.label',
        )}
        {...rest}
      />

      {menuOpen && (
        <DatepickerWithOptions
          referenceElement={containerRef}
          interactionType={InteractionType.Task}
          value={value}
          onChange={_onChange}
          dateFormat={dateFormat}
          weekStartsOn={weekStartsOn}
          icon={IconOptions.Calendar}
          canRemoveValue={canRemove}
          onOutside={closeMenu}
        />
      )}
    </Styled.Container>
  );
};
